import { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { MapPin } from 'lucide-react';
import { configApi } from '@shared/services/configApi';
import './ClinicMap.css';

interface ClinicMapProps {
  address: string;
  clinicName?: string;
}

interface ClinicMapInnerProps {
  address: string;
  clinicName?: string;
  apiKey: string;
}

const containerStyle = {
  width: '100%',
  height: '250px',
  borderRadius: '8px',
};

const defaultCenter = {
  lat: 39.8283,
  lng: -98.5795, // Center of US
};

// Cache the API key to avoid fetching it multiple times
let cachedApiKey: string | null = null;
let apiKeyPromise: Promise<string | null> | null = null;

async function getApiKey(): Promise<string | null> {
  if (cachedApiKey) return cachedApiKey;
  if (apiKeyPromise) return apiKeyPromise;

  apiKeyPromise = configApi.getGoogleMapsApiKey().then((key) => {
    cachedApiKey = key;
    // Clear the promise cache on failure so subsequent mounts can retry
    if (!key) {
      apiKeyPromise = null;
    }
    return key;
  });

  return apiKeyPromise;
}

// Inner component that uses the Google Maps hook - only rendered when API key is available
function ClinicMapInner({ address, clinicName, apiKey }: Readonly<ClinicMapInnerProps>) {
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState<google.maps.LatLngLiteral | null>(null);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
  });

  // Geocode address whenever it changes
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (address && window.google && isLoaded) {
      // Reset error state when address changes
      setGeocodeError(null);

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const location = results[0].geometry.location;
          const position = {
            lat: location.lat(),
            lng: location.lng(),
          };
          setMapCenter(position);
          setMarkerPosition(position);

          // Update map center and zoom if map is already loaded
          if (mapRef.current) {
            mapRef.current.setCenter(position);
            mapRef.current.setZoom(15);
          }
        } else {
          setGeocodeError('Could not locate address on map');
        }
      });
    }
  }, [address, isLoaded]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    // If geocoding completed before the map mounted, apply its result now.
    // Without this, a fast geocode callback hits the null-mapRef branch and
    // the view stays at the default center/zoom until the next address change.
    if (markerPosition) {
      map.setCenter(markerPosition);
      map.setZoom(15);
    }
  }, [markerPosition]);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  // Loading error
  if (loadError) {
    return (
      <div className="clinic-map-placeholder">
        <MapPin size={32} className="map-placeholder-icon" />
        <p>Error loading map</p>
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="map-link"
        >
          View on Google Maps
        </a>
      </div>
    );
  }

  // Still loading Google Maps
  if (!isLoaded) {
    return (
      <div className="clinic-map-loading">
        <div className="loading-spinner-small"></div>
        <p>Loading map...</p>
      </div>
    );
  }

  // If geocoding failed, show a fallback view instead of the map
  if (geocodeError) {
    return (
      <div className="clinic-map-container">
        <div className="clinic-map-placeholder">
          <MapPin size={32} className="map-placeholder-icon" />
          <p>Map preview unavailable</p>
          <div className="map-links">
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="map-link"
            >
              View on Google Maps
            </a>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="map-link"
            >
              Get Directions
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="clinic-map-container">
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={mapCenter}
        zoom={4}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          disableDefaultUI: true,
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        }}
      >
        {markerPosition && (
          <Marker
            position={markerPosition}
            title={clinicName || address}
          />
        )}
      </GoogleMap>
      <a
        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="map-directions-link"
      >
        Get Directions
      </a>
    </div>
  );
}

export default function ClinicMap({ address, clinicName }: Readonly<ClinicMapProps>) {
  const [apiKey, setApiKey] = useState<string | null>(cachedApiKey);
  const [apiKeyLoading, setApiKeyLoading] = useState(!cachedApiKey);

  // Fetch API key from backend on mount
  useEffect(() => {
    if (!cachedApiKey) {
      getApiKey().then((key) => {
        setApiKey(key);
        setApiKeyLoading(false);
      });
    }
  }, []);

  // Still loading API key
  if (apiKeyLoading) {
    return (
      <div className="clinic-map-loading">
        <div className="loading-spinner-small"></div>
        <p>Loading map...</p>
      </div>
    );
  }

  // No API key available
  if (!apiKey) {
    return (
      <div className="clinic-map-placeholder">
        <MapPin size={32} className="map-placeholder-icon" />
        <p>Map unavailable</p>
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="map-link"
        >
          View on Google Maps
        </a>
      </div>
    );
  }

  // Render the inner map component only when we have an API key
  return <ClinicMapInner address={address} clinicName={clinicName} apiKey={apiKey} />;
}
